// Top Scorers (League + Club) — types + pure logic (Phase 3).
//
// Both widgets are ranked horizontal bar charts: one row per player, sorted by
// goals descending, bar width proportional to the leader. League ranks every
// player in the division (fixture teams highlighted); Club ranks one team's
// scorers (bar tinted the team colour, zero-goal players dropped). The D3
// enter/update/exit that animates rows + bar widths lives in the component
// (React owns the container, D3 owns the rows); everything decidable without a
// DOM lives here, pure and unit-tested: which goal tally to show for the current
// venue/timeline, the sorted+shaped rows, and each bar's width.

/** Per-player goal tallies across venue (total/home/away) × window (season/5/10). */
export interface ScorerGoals {
  total: number;
  home: number;
  away: number;
  t5: number;
  h5: number;
  a5: number;
  t10: number;
  h10: number;
  a10: number;
}

/** A league scorer carries its club (used to highlight the fixture's teams). */
export interface LeagueScorer extends ScorerGoals {
  name: string;
  team: string;
}

/** A club scorer is just a name + tallies (the club is the map key). */
export interface ClubScorer extends ScorerGoals {
  name: string;
}

/** League dataset: a flat ranked list. */
export type TopScorersLeagueData = LeagueScorer[];

/** Club dataset: scorers keyed by full team name. */
export type TopScorersClubData = Record<string, ClubScorer[]>;

/** Venue filter (ha-toggle). */
export type ScorerVenue = "All" | "Home" | "Away";

/** Time window (timeline-toggle): whole season, last 5, or last 10. */
export type ScorerTimeline = "Season" | "5" | "10";

/** One shaped row the component renders. */
export interface ScorerRow {
  name: string;
  goals: number;
  /** League: player's club is one of the fixture's two teams. Always false for Club. */
  highlighted: boolean;
}

/** Result of shaping: the rows (sorted, ready to join) + the leader's goals (bar scale). */
export interface ScorersShape {
  rows: ScorerRow[];
  /** Max goals in the list (bar-width denominator); ≥1 so a scale of 0 never divides. */
  max: number;
}

/** Goals for the selected venue × timeline — the legacy `getG`, verbatim. */
export function selectGoals(
  s: ScorerGoals,
  venue: ScorerVenue,
  timeline: ScorerTimeline
): number {
  const total = timeline === "5" ? s.t5 : timeline === "10" ? s.t10 : s.total;
  const home = timeline === "5" ? s.h5 : timeline === "10" ? s.h10 : s.home;
  const away = timeline === "5" ? s.a5 : timeline === "10" ? s.a10 : s.away;
  return venue === "Home" ? home : venue === "Away" ? away : total;
}

/** Bar width as a percentage string (min 5% so a tiny tally is still visible),
 *  matching the legacy `Math.max(5, (g / max) * 100).toFixed(1)`. */
export function barWidthPct(goals: number, max: number): string {
  return Math.max(5, (goals / max) * 100).toFixed(1);
}

/** Shape the league list: sorted by the current tally descending, fixture-team
 *  players flagged for highlighting. `filterZero` drops players with no tally in
 *  the window — Top Scorers keeps everyone (the whole division is shown), Assists
 *  filters (a long tail of zero-assist players would be noise). */
export function shapeLeagueScorers(
  data: readonly LeagueScorer[],
  venue: ScorerVenue,
  timeline: ScorerTimeline,
  fixtureTeams: readonly string[],
  filterZero = false
): ScorersShape {
  const mapped = data.map((s) => ({
    name: s.name,
    goals: selectGoals(s, venue, timeline),
    highlighted: fixtureTeams.includes(s.team),
  }));
  const rows = (filterZero ? mapped.filter((r) => r.goals > 0) : mapped).sort(
    (a, b) => b.goals - a.goals
  );
  return { rows, max: rows[0] ? rows[0].goals : 1 };
}

/** Shape one club's list: drop players with no goals in the window, sort
 *  descending. Never highlighted (the bar carries the team colour instead). */
export function shapeClubScorers(
  data: readonly ClubScorer[],
  venue: ScorerVenue,
  timeline: ScorerTimeline
): ScorersShape {
  const rows = data
    .map((s) => ({
      name: s.name,
      goals: selectGoals(s, venue, timeline),
      highlighted: false,
    }))
    .filter((r) => r.goals > 0)
    .sort((a, b) => b.goals - a.goals);
  return { rows, max: rows[0] ? rows[0].goals : 1 };
}
